import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import React, { FC, useState } from 'react';
import { useQuery } from 'react-query';
import {
  AuditEntryType,
  DisclosureAuditEntryType,
  GetCaseAuditEntriesDocument,
  GetCaseAuditEntriesQuery,
  GetCaseAuditEntriesQueryVariables,
  useDisclosureAuditByIdQuery,
} from 'src/graphql';
import { fetcher } from 'src/utils/fetch';
import * as styles from './CaseAuditLog.styles';

interface CaseAuditLogProps {
  caseId?: string;
  responseId?: string;
}

const COLUMNS = ['User', 'Event', 'Timestamp'];
const DEFAULT_ROWS_PER_PAGE = 25;

const formatTimestamp = (date: string): string =>
  dayjs(date).format('YYYY-MM-DD HH:mm');

const getUserName = (
  entry: AuditEntryType | DisclosureAuditEntryType,
): string =>
  (entry as AuditEntryType).User?.name ??
  (entry as AuditEntryType).User?.displayName ??
  (entry as DisclosureAuditEntryType).user?.name ??
  'System';

export const CaseAuditLog: FC<CaseAuditLogProps> = ({ caseId, responseId }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const { data: caseData, isLoading: isCaseLoading } =
    useQuery<GetCaseAuditEntriesQuery>({
      queryKey: ['GetCaseAuditEntries', { case: caseId, page, rowsPerPage }],
      queryFn: () =>
        fetcher<GetCaseAuditEntriesQuery, GetCaseAuditEntriesQueryVariables>(
          GetCaseAuditEntriesDocument,
          {
            case: caseId ?? '',
            pagination: { limit: rowsPerPage, offset: page * rowsPerPage },
          },
        )(),
      keepPreviousData: true,
      enabled: Boolean(caseId),
    });

  const { data: responseData, isLoading: isResponseDataLoading } =
    useDisclosureAuditByIdQuery(
      { responseId: responseId ?? '' },
      { enabled: Boolean(responseId) },
    );

  const totalCount =
    caseData?.getCaseAuditEntries?.count ??
    responseData?.disclosureAuditById?.pagination?.totalItems ??
    0;
  const caseDataEntries = [
    ...((caseData?.getCaseAuditEntries?.items ?? []) as AuditEntryType[]),
  ];
  const responseDataEntries = responseData?.disclosureAuditById?.entries ?? [];
  const entries =
    caseDataEntries.length > 0 ? caseDataEntries : responseDataEntries;

  if (isCaseLoading || isResponseDataLoading) {
    return (
      <Box sx={styles.loading} data-testid="case-audit-log-loading">
        <CircularProgress />
      </Box>
    );
  }

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box data-testid="case-audit-log">
      <Typography component="h3" sx={styles.heading}>
        Audit log
      </Typography>
      <Box sx={styles.tableContainer}>
        <Table aria-label="Case audit log" size="small" sx={styles.table}>
          <TableHead>
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell key={col}>{col}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>{getUserName(entry)}</TableCell>
                <TableCell>
                  {(entry as AuditEntryType).DescriptiveText ??
                    (entry as DisclosureAuditEntryType).eventType?.replaceAll(
                      '_',
                      ' ',
                    ) ??
                    ''}
                </TableCell>
                <TableCell>
                  {entry.createdAt ? formatTimestamp(entry.createdAt) : ''}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[25, 50, 100]}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        labelRowsPerPage="Rows"
        data-testid="case-audit-log-pagination"
        sx={styles.pagination}
      />
    </Box>
  );
};
